import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LinkIcon from '@mui/icons-material/Link';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { WizardPackage } from '@/types';
import { formatPrice } from '@/utils/price.utils';

// Prezzo mostrato solo se il pacchetto costa di più (priceDiff > 0): "+ N €" con nota informativa.
const PRICE_INFO = 'Puoi monitorare le tue aggiunte nella sezione Riepilogo. I prezzi sono IVA esclusa.';

interface OptionCardProps {
  pkg: WizardPackage;
  selected: boolean;
  onSelect: (packageId: number) => void;
}

// Riproduce il pattern "OptionCard" del legacy: card bordata, fascia prezzo in
// basso separata da un bordo, badge di stato selezionato via bottone pillola.
const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.paper,
  border: `${selected ? 2 : 1}px solid`,
  borderColor: selected ? theme.palette.secondary.main : theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  transition: 'border-color 200ms ease, transform 200ms ease',
  '&:hover': {
    borderColor: theme.palette.secondary.main,
  },
}));

// Immagine di copertina (o fallback neutro) in cima alla card, sopra il badge/contenuto.
const CardMediaBox = styled(Box)(({ theme }) => ({
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  backgroundColor: theme.palette.grey[100],
}));

function packageTitle(pkg: WizardPackage): string {
  return pkg.title ?? pkg.name ?? pkg.materiotecaName ?? `Pacchetto ${pkg.packageId}`;
}

export function OptionCard({ pkg, selected, onSelect }: OptionCardProps) {
  const title = packageTitle(pkg);
  const showSubtitle = pkg.materiotecaName && pkg.materiotecaName !== title;

  return (
    <CardRoot
      selected={selected}
      onClick={() => onSelect(pkg.packageId)}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${title}${pkg.priceDiff != null && pkg.priceDiff > 0 ? ` — + ${formatPrice(pkg.priceDiff)}` : ''}${selected ? ' (selezionato)' : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(pkg.packageId);
        }
      }}
    >
      <CardMediaBox>
        {pkg.imageUrl ? (
          <Box
            component="img"
            src={pkg.imageUrl}
            alt={title}
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              color: 'text.disabled',
              px: 1.5,
              textAlign: 'center',
            }}
          >
            <ImageOutlinedIcon fontSize="medium" />
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
          </Box>
        )}
      </CardMediaBox>

      {pkg.subsequentChoiceWarning && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            bgcolor: 'secondary.light',
            color: 'secondary.contrastText',
          }}
        >
          <LinkIcon fontSize="inherit" sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
            Questa scelta condiziona i passi successivi
          </Typography>
        </Box>
      )}

      <Box sx={{ p: 2, flex: 1 }}>
        <Typography variant="body1" fontWeight={500}>
          {title}
        </Typography>
        {showSubtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {pkg.materiotecaName}
          </Typography>
        )}
        {pkg.caption && (
          <Typography
            variant="body2"
            sx={{ mt: 1, fontStyle: 'italic', color: 'secondary.dark' }}
          >
            {pkg.caption}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        {pkg.priceDiff != null && pkg.priceDiff > 0 ? (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title={PRICE_INFO}>
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#A15117' }}>
              + {formatPrice(pkg.priceDiff)}
            </Typography>
          </Box>
        ) : (
          <span />
        )}
        <Chip
          size="small"
          icon={
            selected ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" />
            )
          }
          label={selected ? 'Selezionato' : 'Seleziona'}
          color={selected ? 'secondary' : 'default'}
          variant={selected ? 'filled' : 'outlined'}
          sx={{ pointerEvents: 'none', fontWeight: 500 }}
        />
      </Box>
    </CardRoot>
  );
}
